import { z } from 'zod';
import { MEMBER_ROLES, PLANS, WORKSPACE_KINDS } from '../constants.js';
import { auditFields, emailSchema, shortText, ulidSchema } from '../common.js';

export const workspaceSchema = z.object({
  type: z.literal('Workspace'),
  wsId: ulidSchema,
  name: shortText,
  kind: z.enum(WORKSPACE_KINDS),
  plan: z.enum(PLANS).default('free'),
  stripeCustomerId: z.string().max(64).optional(),
  archivedAt: z.string().optional(),
  ...auditFields,
});
export type Workspace = z.infer<typeof workspaceSchema>;

export const createWorkspaceSchema = z.object({
  name: shortText,
  kind: z.enum(WORKSPACE_KINDS).default('couple'),
});
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceSchema>;

export const membershipSchema = z.object({
  type: z.literal('Membership'),
  wsId: ulidSchema,
  userId: z.string(),
  email: emailSchema,
  name: z.string().max(200).optional(),
  role: z.enum(MEMBER_ROLES),
  invitedBy: z.string().optional(),
  acceptedAt: z.string().optional(),
  /**
   * When this person last opened the notification bell. Per member, because
   * "what is new" is a different question for each of two people looking at the
   * same wedding.
   */
  activitySeenAt: z.string().optional(),
  ...auditFields,
});
export type Membership = z.infer<typeof membershipSchema>;

/**
 * A grant waiting for its person. Written only when the invitee's Cognito `sub`
 * cannot be determined at invite time; `claimInvites` turns it into a Membership
 * on their first authenticated request.
 */
export const workspaceInviteSchema = z.object({
  type: z.literal('WorkspaceInvite'),
  wsId: ulidSchema,
  email: emailSchema,
  name: shortText.optional(),
  role: z.enum(MEMBER_ROLES),
  invitedBy: z.string(),
  /** Unix seconds, for the table's TTL. A janitor, not an authorization boundary. */
  expiresAt: z.number().int().positive(),
  ...auditFields,
});
export type WorkspaceInvite = z.infer<typeof workspaceInviteSchema>;

export const inviteMemberSchema = z.object({
  email: emailSchema,
  /** Optional, so a new Cognito account can be created with a display name. */
  name: shortText.optional(),
  role: z.enum(MEMBER_ROLES).refine((r) => r !== 'owner', 'a workspace has exactly one owner'),
});
export type InviteMemberInput = z.infer<typeof inviteMemberSchema>;

export const updateMemberSchema = z.object({
  role: z.enum(MEMBER_ROLES).refine((r) => r !== 'owner', 'ownership transfer is a separate flow'),
});
export type UpdateMemberInput = z.infer<typeof updateMemberSchema>;
